import { Fragment, useState, useRef, useCallback } from 'react';
// useNavigate retained for potential re-enable of Edit button (commented out below)
// import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// Pencil retained for potential re-enable of Edit button (commented out below)
import { Check, Trash2, AlertCircle, AlertTriangle } from 'lucide-react';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import type {
  QuoteWithDetails,
  PackageWithDetails,
  AdditionalChargeType,
} from '@everplan/shared';
import { CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { NumericFormat } from 'react-number-format';
import { formatCents } from '@/lib/format';
import { computePackageTotal, reconcileStatedTotal } from '@/lib/reconcile';
import { client } from '@/lib/api';
import CollapsibleCard from '@/components/CollapsibleCard';
import DocumentSplit from '@/components/DocumentSplit';
import {
  ChargeFormulaTooltip,
  type ChargeFormulaTooltipContext,
} from '@/components/quote-editor/ChargeFormulaTooltip';

// A package is considered "edited" when any of its line items or charges has
// a server-side updatedAt strictly later than its createdAt. Drizzle's
// `defaultNow()` sets both equal at insert; `$onUpdate(() => new Date())`
// only bumps updatedAt. A 1-second tolerance absorbs any insert-time drift
// between the two columns.
const EDIT_TOLERANCE_MS = 1000;
function isEdited(row: { createdAt: string | Date; updatedAt: string | Date }): boolean {
  const created = new Date(row.createdAt).getTime();
  const updated = new Date(row.updatedAt).getTime();
  return updated - created > EDIT_TOLERANCE_MS;
}
function packageHasBeenEdited(pkg: PackageWithDetails): boolean {
  return pkg.lineItems.some(isEdited) || pkg.additionalCharges.some(isEdited);
}

function buildPackageContext(pkg: PackageWithDetails): ChargeFormulaTooltipContext {
  let lineItemTotal = 0;
  const sectionTotals: Record<string, number> = {};
  for (const li of pkg.lineItems) {
    const qty = li.quantity ?? 1;
    const lineValue = qty * li.unitCost;
    lineItemTotal += lineValue;
    if (li.sectionNormalized) {
      sectionTotals[li.sectionNormalized] = (sectionTotals[li.sectionNormalized] ?? 0) + lineValue;
    }
  }
  const siblingResolvedAmounts: Record<string, number> = {};
  for (const ch of pkg.additionalCharges) {
    siblingResolvedAmounts[ch.id] = ch.amount;
  }
  return {
    lineItemTotal,
    addOnTotal: 0,
    sectionTotals,
    siblingResolvedAmounts,
  };
}

function computeAddOnsTotal(addOns: QuoteWithDetails['addOns']): number {
  return addOns.reduce(
    (sum, ao) => sum + (ao.quantity != null ? ao.quantity * ao.unitCost : 0),
    0,
  );
}

function computeQuoteTotal(quote: QuoteWithDetails): { min: number; max: number } {
  const aoTotal = computeAddOnsTotal(quote.addOns);
  const quoteChargeTotal = quote.additionalCharges.reduce((sum, ch) => sum + ch.amount, 0);
  const base = aoTotal + quoteChargeTotal;
  const selected = quote.packages.filter((pkg) => pkg.isSelected);
  if (selected.length > 0) {
    const sum = selected.reduce(
      (s, pkg) => s + computePackageTotal(pkg as PackageWithDetails),
      0,
    );
    return { min: sum + base, max: sum + base };
  }
  const totals = quote.packages.map((pkg) =>
    computePackageTotal(pkg as PackageWithDetails),
  );
  if (totals.length === 0) return { min: base, max: base };
  return { min: Math.min(...totals) + base, max: Math.max(...totals) + base };
}

function formatQuoteTotal({ min, max }: { min: number; max: number }): string {
  return min === max ? formatCents(min) : `${formatCents(min)} – ${formatCents(max)}`;
}

const CHARGE_TYPE_LABELS: Record<AdditionalChargeType, string> = {
  delivery: 'Delivery',
  labor: 'Labor',
  tax: 'Tax',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// Inline editable cells
// ---------------------------------------------------------------------------

function useDebounceUpdate<T>(
  onSave: (value: T) => void,
  delay = 500,
) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  return useCallback(
    (value: T) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => onSave(value), delay);
    },
    [onSave, delay],
  );
}

function InlineQty({
  value,
  onSave,
}: {
  value: number | null;
  onSave: (qty: number | null) => void;
}) {
  const [local, setLocal] = useState(value);
  const debouncedSave = useDebounceUpdate(onSave);

  return (
    <input
      type="number"
      min={0}
      value={local ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        const next = v === '' ? null : parseInt(v, 10) || null;
        setLocal(next);
        debouncedSave(next);
      }}
      placeholder="—"
      className="h-7 w-16 rounded-none border-0 border-b border-dashed border-border bg-transparent px-2 py-1 text-sm text-right tabular-nums hover:border-border focus:border-solid focus:border-primary focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
  );
}

function InlinePrice({
  valueCents,
  onSave,
}: {
  valueCents: number;
  onSave: (cents: number) => void;
}) {
  const [local, setLocal] = useState(valueCents);
  const debouncedSave = useDebounceUpdate(onSave);

  return (
    <NumericFormat
      thousandSeparator=","
      decimalScale={2}
      fixedDecimalScale
      allowNegative={false}
      prefix="$"
      value={local / 100}
      onValueChange={({ floatValue }) => {
        const next = floatValue !== undefined ? Math.round(floatValue * 100) : 0;
        setLocal(next);
        debouncedSave(next);
      }}
      className="h-7 w-24 rounded-none border-0 border-b border-dashed border-border bg-transparent px-2 py-1 text-sm text-right tabular-nums hover:border-border focus:border-solid focus:border-primary focus:outline-none"
    />
  );
}

// ---------------------------------------------------------------------------
// QuoteCard
// ---------------------------------------------------------------------------

interface QuoteCardProps {
  quote: QuoteWithDetails;
  itemId: string;
  projectId: string;
  isOnlyQuote?: boolean;
  isUrlExpanded?: boolean;
  onToggleExpand?: () => void;
}

export default function QuoteCard({
  quote,
  itemId,
  projectId,
  isOnlyQuote = false,
  isUrlExpanded,
  onToggleExpand,
}: QuoteCardProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(isOnlyQuote);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);

  // Freeze the per-package reconcile state at first render. Re-running it on
  // every keystroke is what was causing the alert to toggle in/out and shift
  // scroll while the planner edited line items. Once edits start, the live
  // computed total is the planner's intent — not a signal that the LLM
  // mis-extracted. The banner only fires on extraction-time mismatches.
  const initialReconcileRef = useRef<Map<string, ReturnType<typeof reconcileStatedTotal>>>(
    new Map(),
  );
  for (const pkg of quote.packages) {
    if (!initialReconcileRef.current.has(pkg.id)) {
      const statedTotal =
        (pkg as PackageWithDetails & { statedTotal?: number | null }).statedTotal ?? null;
      initialReconcileRef.current.set(
        pkg.id,
        reconcileStatedTotal(statedTotal, computePackageTotal(pkg as PackageWithDetails)),
      );
    }
  }

  const effectiveExpanded = isUrlExpanded !== undefined ? isUrlExpanded : expanded;
  const handleHeaderClick = () => {
    if (onToggleExpand) onToggleExpand();
    else setExpanded((v) => !v);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['item', itemId] });
    queryClient.invalidateQueries({ queryKey: ['project', projectId] });
  };

  const selectQuote = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/quotes/${quote.id}/select`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to select quote');
    },
    onSuccess: invalidate,
  });

  const deleteQuote = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/quotes/${quote.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete quote');
    },
    onSuccess: invalidate,
  });

  // Optimistic-update path. Patches the React Query cache in place so we don't
  // refetch on every keystroke (which causes layout/scroll shifts when the
  // user is editing inside a long quote summary).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type ItemCache = any;
  const updateLineItem = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; quantity?: number | null; unitCost?: number }) => {
      const res = await client.api['line-items'][':id'].$put({ param: { id }, json: data });
      if (!res.ok) throw new Error('Failed to update line item');
      return res.json() as Promise<{
        id: string;
        quantity: number | null;
        unitCost: number;
        drifted?: Array<{ chargeId: string; oldAmount: number; newAmount: number }>;
      }>;
    },
    onMutate: async ({ id, quantity, unitCost }) => {
      await queryClient.cancelQueries({ queryKey: ['item', itemId] });
      const previous = queryClient.getQueryData<ItemCache>(['item', itemId]);
      queryClient.setQueryData<ItemCache>(['item', itemId], (old: ItemCache) => {
        if (!old?.quotes) return old;
        return {
          ...old,
          quotes: old.quotes.map((q: ItemCache) =>
            q.id !== quote.id
              ? q
              : {
                  ...q,
                  packages: q.packages.map((p: ItemCache) => ({
                    ...p,
                    lineItems: p.lineItems.map((li: ItemCache) =>
                      li.id !== id
                        ? li
                        : {
                            ...li,
                            ...(quantity !== undefined ? { quantity } : {}),
                            ...(unitCost !== undefined ? { unitCost } : {}),
                            // Bump updatedAt so packageHasBeenEdited() flips
                            // immediately, before the server response lands.
                            updatedAt: new Date().toISOString(),
                          },
                    ),
                  })),
                },
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(['item', itemId], ctx.previous);
      }
    },
    onSuccess: (response) => {
      // Apply server-reported drifted charge amounts to the cache.
      if (response.drifted && response.drifted.length > 0) {
        const driftMap = new Map(response.drifted.map((d) => [d.chargeId, d.newAmount]));
        queryClient.setQueryData<ItemCache>(['item', itemId], (old: ItemCache) => {
          if (!old?.quotes) return old;
          return {
            ...old,
            quotes: old.quotes.map((q: ItemCache) =>
              q.id !== quote.id
                ? q
                : {
                    ...q,
                    packages: q.packages.map((p: ItemCache) => ({
                      ...p,
                      additionalCharges: p.additionalCharges.map((ch: ItemCache) =>
                        driftMap.has(ch.id) ? { ...ch, amount: driftMap.get(ch.id) } : ch,
                      ),
                    })),
                  },
            ),
          };
        });
      }
      // Refresh budget rollups on the project page (different query).
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const updateAddOn = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; quantity?: number | null; unitCost?: number }) => {
      const res = await client.api['add-ons'][':id'].$put({ param: { id }, json: data });
      if (!res.ok) throw new Error('Failed to update add-on');
    },
    onMutate: async ({ id, quantity, unitCost }) => {
      await queryClient.cancelQueries({ queryKey: ['item', itemId] });
      const previous = queryClient.getQueryData<ItemCache>(['item', itemId]);
      queryClient.setQueryData<ItemCache>(['item', itemId], (old: ItemCache) => {
        if (!old?.quotes) return old;
        return {
          ...old,
          quotes: old.quotes.map((q: ItemCache) =>
            q.id !== quote.id
              ? q
              : {
                  ...q,
                  addOns: q.addOns.map((ao: ItemCache) =>
                    ao.id !== id
                      ? ao
                      : {
                          ...ao,
                          ...(quantity !== undefined ? { quantity } : {}),
                          ...(unitCost !== undefined ? { unitCost } : {}),
                          updatedAt: new Date().toISOString(),
                        },
                  ),
                },
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(['item', itemId], ctx.previous);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const togglePackage = useMutation({
    mutationFn: async (packageId: string) => {
      const pkg = quote.packages.find((p) => p.id === packageId);
      if (!pkg) return;
      const next = !pkg.isSelected;
      await client.api.packages[':id'].$put({ param: { id: packageId }, json: { isSelected: next } });
      if (next && !quote.isSelected) {
        const res = await fetch(`/api/quotes/${quote.id}/select`, { method: 'POST' });
        if (!res.ok) throw new Error('Failed to select quote');
      }
    },
    onSuccess: invalidate,
  });

  const revertQuote = useMutation({
    mutationFn: async () => {
      const res = await client.api.quotes[':id'].revert.$post({ param: { id: quote.id } });
      if (!res.ok) throw new Error('Failed to revert quote');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const total = computeQuoteTotal(quote);
  const totalDisplay = formatQuoteTotal(total);

  return (
    <CollapsibleCard
      header={
        <div className="flex flex-wrap items-center gap-2">
          {!isOnlyQuote && (
            <button
              type="button"
              role="checkbox"
              aria-checked={quote.isSelected}
              aria-label={quote.isSelected ? 'Deselect this quote' : 'Select this quote'}
              disabled={selectQuote.isPending}
              onClick={(e) => {
                e.stopPropagation();
                selectQuote.mutate();
              }}
              className={`inline-flex size-4 shrink-0 items-center justify-center rounded border transition-colors ${
                quote.isSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground/40 hover:border-foreground'
              }`}
            >
              {quote.isSelected && <Check className="size-2.5" />}
            </button>
          )}
          <span className="font-medium text-sm truncate">{quote.name}</span>
          {quote.isSelected && !isOnlyQuote && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-primary-foreground">
              <Check className="size-3" />
              Selected
            </span>
          )}
          {(quote.confidence === 'medium' || quote.confidence === 'low') && (
            <span
              title={`Extraction confidence: ${quote.confidence}. Double-check the line items below.`}
              className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400"
            >
              <AlertCircle className="size-3" />
              Verify
            </span>
          )}
        </div>
      }
      trailing={
        <span className="label-caps text-xs tabular-nums text-muted-foreground shrink-0">
          {totalDisplay}
        </span>
      }
      expanded={effectiveExpanded}
      onToggle={handleHeaderClick}
    >
      <DocumentSplit documentUrl={quote.sourceDocumentUrl} documentMimeType={quote.sourceDocumentMimeType}>
        <ExpandedBody />
      </DocumentSplit>
    </CollapsibleCard>
  );

  function ExpandedBody() {
    return (
        <CardContent className="space-y-6 pb-5 pt-5">
          {(quote.packages as PackageWithDetails[]).map((pkg) => {
            const formulaCtx = buildPackageContext(pkg);
            const chargeLabels: Record<string, string> = Object.fromEntries(
              pkg.additionalCharges.map((c) => [c.id, c.description ?? CHARGE_TYPE_LABELS[c.type]]),
            );
            return (
            <div key={pkg.id} className={`space-y-2 ${quote.packages.length > 1 ? 'rounded-xl border border-border p-4' : ''}`}>
              {/* Package name (only show if more than one package) */}
              {quote.packages.length > 1 && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={pkg.isSelected}
                    onChange={() => togglePackage.mutate(pkg.id)}
                    className="h-4 w-4 shrink-0 rounded accent-primary cursor-pointer"
                  />
                  <p className="text-sm font-semibold flex-1">
                    {pkg.name}
                  </p>
                  <span className="text-sm tabular-nums font-medium">
                    {formatCents(computePackageTotal(pkg))}
                  </span>
                </div>
              )}

              {/* Package description / what's included */}
              {pkg.description && (
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {pkg.description}
                </p>
              )}

              {/* Top-of-package alert. Two mutually-exclusive states:
                  - Quote edited in the app: server-side updatedAt > createdAt
                    on any line item or charge. Suppresses the parse-time
                    mismatch since the live state is now the planner's intent.
                  - Untouched + parse-time totals mismatch: frozen extraction
                    quality warning (numbers from first-render reconcile). */}
              {(() => {
                if (packageHasBeenEdited(pkg)) {
                  return (
                    <Alert>
                      <AlertCircle className="size-4" />
                      <AlertTitle>Quote edited in the app</AlertTitle>
                      <AlertDescription>
                        <span>
                          This quote's line items or charges have been changed since the
                          document was uploaded. Totals reflect your edits, not the
                          original document.
                        </span>
                        {(quote as QuoteWithDetails & { hasExtractionSnapshot?: boolean }).hasExtractionSnapshot && (
                          <div className="mt-2">
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => setConfirmRevert(true)}
                              disabled={revertQuote.isPending}
                            >
                              {revertQuote.isPending ? 'Reverting…' : 'Revert to original'}
                            </Button>
                            <DeleteConfirmDialog
                              open={confirmRevert}
                              onOpenChange={setConfirmRevert}
                              onConfirm={() => revertQuote.mutate()}
                              isPending={revertQuote.isPending}
                              title="Revert to original?"
                              description="This will revert all packages, line items, and charges in this quote to the original document state. Your edits will be discarded."
                            />
                          </div>
                        )}
                      </AlertDescription>
                    </Alert>
                  );
                }
                const r = initialReconcileRef.current.get(pkg.id);
                if (!r || r.status !== 'mismatch') return null;
                return (
                  <Alert variant="destructive">
                    <AlertTriangle className="size-4" />
                    <AlertTitle>Totals don't match the document</AlertTitle>
                    <AlertDescription>
                      Document shows {formatCents(r.stated!)} but line items + charges sum to{' '}
                      {formatCents(r.computed)} (
                      {r.diff >= 0 ? '+' : '−'}{formatCents(Math.abs(r.diff))}).
                      Review the extracted items for missing or duplicated rows.
                    </AlertDescription>
                  </Alert>
                );
              })()}

              {/* Line items table */}
              {pkg.lineItems.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="label-caps pb-1.5 pr-3 text-xs font-medium text-muted-foreground">Name</th>
                        <th className="label-caps pb-1.5 pr-3 text-xs font-medium text-muted-foreground w-16">Qty</th>
                        <th className="label-caps pb-1.5 pr-3 text-xs font-medium text-muted-foreground w-24">Unit Cost</th>
                        <th className="label-caps pb-1.5 text-xs font-medium text-muted-foreground text-right w-20">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pkg.lineItems.map((li, idx) => {
                        const lineTotal = li.quantity != null ? li.quantity * li.unitCost : li.unitCost;
                        const prevSection = idx > 0 ? pkg.lineItems[idx - 1]?.section : undefined;
                        const showSection = li.section && li.section !== prevSection;
                        return (
                          <Fragment key={li.id}>
                            {showSection && (
                              <tr>
                                <td colSpan={4} className="pt-4 pb-1">
                                  <span className="text-[11px] font-semibold tracking-wide text-foreground/70">
                                    {li.section}
                                  </span>
                                </td>
                              </tr>
                            )}
                            <tr className="border-b last:border-0">
                              <td className="py-1.5 pr-3">
                                <div>
                                  <span>{li.name}</span>
                                  {li.description && (
                                    <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                                      {li.description}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="py-1.5 pr-3 w-16">
                                <InlineQty
                                  key={li.id + '-qty'}
                                  value={li.quantity}
                                  onSave={(qty) => updateLineItem.mutate({ id: li.id, quantity: qty })}
                                />
                              </td>
                              <td className="py-1.5 pr-3 w-24">
                                <InlinePrice
                                  key={li.id + '-price'}
                                  valueCents={li.unitCost}
                                  onSave={(cents) => updateLineItem.mutate({ id: li.id, unitCost: cents })}
                                />
                              </td>
                              <td className="py-1.5 w-20 text-right text-sm tabular-nums text-muted-foreground">
                                {formatCents(lineTotal)}
                              </td>
                            </tr>
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Additional charges */}
              {pkg.additionalCharges.length > 0 && (
                <div className="space-y-1 pt-1">
                  <p className="label-caps text-xs text-muted-foreground">Additional Charges</p>
                  <ul className="space-y-1">
                    {pkg.additionalCharges.map((ch) => (
                      <ChargeFormulaTooltip
                        key={ch.id}
                        calcMethod={ch.calcMethod}
                        rateBps={ch.rateBps}
                        basis={ch.basis}
                        packageContext={formulaCtx}
                        chargeLabels={chargeLabels}
                      >
                        <li className="flex items-center gap-2 text-sm">
                          <span className="inline-flex items-center rounded-full bg-[var(--color-secondary-container)] px-2 py-0.5 text-xs font-medium">
                            {CHARGE_TYPE_LABELS[ch.type]}
                          </span>
                          {ch.description && (
                            <span className="text-muted-foreground flex-1 truncate">{ch.description}</span>
                          )}
                          <span className="ml-auto tabular-nums font-medium">{formatCents(ch.amount)}</span>
                        </li>
                      </ChargeFormulaTooltip>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            );
          })}

          {/* Add-ons */}
          {quote.addOns.length > 0 && (
            <div className="space-y-2">
              <p className="label-caps text-xs text-muted-foreground">Available Add-Ons</p>
              <table className="w-full text-sm">
                <tbody>
                  {quote.addOns.map((ao) => (
                    <tr key={ao.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3">
                        <div className="flex items-center gap-2">
                          <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                            Add-on
                          </span>
                          <span>{ao.name}</span>
                        </div>
                      </td>
                      <td className="py-1.5 pr-3 w-16">
                        <InlineQty
                          key={ao.id + '-qty'}
                          value={ao.quantity}
                          onSave={(qty) => updateAddOn.mutate({ id: ao.id, quantity: qty })}
                        />
                      </td>
                      <td className="py-1.5 pr-3 w-24">
                        <InlinePrice
                          key={ao.id + '-price'}
                          valueCents={ao.unitCost}
                          onSave={(cents) => updateAddOn.mutate({ id: ao.id, unitCost: cents })}
                        />
                      </td>
                      <td className="py-1.5 w-20 text-right tabular-nums text-muted-foreground">
                        {formatCents(ao.quantity != null ? ao.quantity * ao.unitCost : 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Quote-level additional charges (always apply) */}
          {quote.additionalCharges.length > 0 && (
            <div className="space-y-1">
              <p className="label-caps text-xs text-muted-foreground">Required Fees</p>
              <ul className="space-y-1">
                {quote.additionalCharges.map((ch) => (
                  <li key={ch.id} className="flex items-center gap-2 text-sm">
                    <span className="inline-flex items-center rounded-full bg-[var(--color-secondary-container)] px-2 py-0.5 text-xs font-medium">
                      {CHARGE_TYPE_LABELS[ch.type]}
                    </span>
                    {ch.description && (
                      <span className="text-muted-foreground flex-1 truncate">{ch.description}</span>
                    )}
                    <span className="ml-auto tabular-nums font-medium">{formatCents(ch.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quote total */}
          <div className="flex items-center justify-between border-t pt-3">
            <span className="label-caps text-xs text-muted-foreground">Quote Total</span>
            <span className="font-semibold tabular-nums">{totalDisplay}</span>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {/* TODO: re-enable if needed for heavy edits (rename, add rows, etc.) */}
            {/*
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                navigate(`/projects/${projectId}/items/${itemId}/quote-editor?quoteId=${quote.id}`)
              }
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
            */}
            {!quote.isSelected && !isOnlyQuote && (
              <Button
                size="sm"
                onClick={() => selectQuote.mutate()}
                disabled={selectQuote.isPending}
              >
                <Check className="size-3.5" />
                Select Quote
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="size-3.5" />
              Delete
            </Button>
            <DeleteConfirmDialog
              open={confirmDelete}
              onOpenChange={setConfirmDelete}
              onConfirm={() => deleteQuote.mutate()}
              isPending={deleteQuote.isPending}
              title="Delete quote?"
              description="This will permanently delete this quote and all its line items."
            />
          </div>
        </CardContent>
    );
  }
}
